// hooks/useAdActions.tsx

'use client';

/* eslint-disable no-underscore-dangle */
import { ApolloError, useApolloClient } from '@apollo/client';
import { useRouter } from 'next/navigation';

import {
  Ad,
  AdStatusEnum,
  AdTypeEnum,
  useDeleteAdMutation,
  useSwitchActivateAdMutation
} from '@/__generated_graphql__/types';
import { useModal } from '@/components/AdvertisePayments/hooks/useModal';
import { GET_PROFILE_ADS } from '@/graphql/queries/ad.queries';
import { GET_ADS_STATE_COUNTS } from '@/graphql/queries/profile.queries';
import { GET_RESUME_BY_ID } from '@/graphql/queries/resume.queries';
import { GET_VACANCY_BY_ID } from '@/graphql/queries/vacancy';
import { useTopProgressBar } from '@/providers/TopProgressBarProvider';
import { AdStatus, CardAd } from '@/types';
import { logError } from '@/utils/log-error';

import { useCopyUrl } from './useCopyUrl';

const getRefetchQueries = (adId: string, adType: AdTypeEnum) => {
  switch (adType) {
    case AdTypeEnum.Resume: {
      return [
        {
          query: GET_RESUME_BY_ID,
          variables: {
            id: adId
          }
        }
      ];
    }
    case AdTypeEnum.Vacancy: {
      return [
        {
          query: GET_VACANCY_BY_ID,
          variables: {
            id: adId
          }
        }
      ];
    }
    default: {
      return [];
    }
  }
};

export const useAdActions = (ad: CardAd | Ad | null) => {
  const router = useRouter();
  const client = useApolloClient();

  const [deleteAd, { loading: deleteLoading }] = useDeleteAdMutation();

  const [switchStatusMutation, { loading: switchLoading }] =
    useSwitchActivateAdMutation();
  const { track } = useTopProgressBar();

  const mainModal = useModal();
  const confirmModal = useModal();

  const isActionPending = switchLoading || deleteLoading;

  const onDeactivateOrDeleteOpen = () => {
    confirmModal.open();
  };

  const onDeactivateOrDeleteConfirm = async () => {
    if (!ad?.id || !ad.__typename) {
      console.warn('[Ad ID]: Cannot delete ad without valid ID.');
      return;
    }

    const { id, __typename } = ad;
    const adType = AdTypeEnum[__typename];
    const isActive = ad.status === AdStatus.approved;

    const refetchQueries = getRefetchQueries(id, adType);

    confirmModal.close();

    try {
      if (isActive) {
        await track(async () =>
          switchStatusMutation({
            variables: {
              id,
              adType
            },
            refetchQueries,
            awaitRefetchQueries: true
          })
        );

        client
          .refetchQueries({
            include: [GET_ADS_STATE_COUNTS]
          })
          .catch(error => {
            logError(error as ApolloError, {
              context: 'RefetchAdsStateCountsAfterDeactivate',
              adId: id,
              adType
            });
          });

        return;
      }

      const { data } = await track(async () =>
        deleteAd({
          variables: {
            id,
            adType
          }
        })
      );

      const isDeleteSuccess = data?.deleteAd?.status === AdStatusEnum.Success;

      if (!isDeleteSuccess) {
        return;
      }

      client
        .refetchQueries({
          include: [GET_ADS_STATE_COUNTS, GET_PROFILE_ADS]
        })
        .catch(error => {
          logError(error as ApolloError, {
            context: 'RefetchAdsAfterDelete',
            adId: id,
            adType
          });
        });

      router.push('/profile');
    } catch (error) {
      logError(error as ApolloError, {
        context: 'DeleteAd',
        adId: id,
        adType
      });
    }
  };

  const onActivate = async (currentAd?: CardAd) => {
    const computedAd = currentAd?.id ? currentAd : ad;

    if (!computedAd?.id || !computedAd.__typename) return;

    const { id, __typename } = computedAd;
    const adType = AdTypeEnum[__typename];

    if (!computedAd.activatable) {
      mainModal?.open?.();
      return;
    }

    const refetchQueries = getRefetchQueries(id, adType);

    try {
      await track(async () =>
        switchStatusMutation({
          variables: {
            id,
            adType
          },
          refetchQueries,
          awaitRefetchQueries: true
        })
      );

      client
        .refetchQueries({
          include: [GET_ADS_STATE_COUNTS, GET_PROFILE_ADS]
        })
        .catch(error => {
          logError(error as ApolloError, {
            context: 'RefetchAdsAfterActivate',
            adId: id,
            adType
          });
        });

      const basePath = adType === AdTypeEnum.Resume ? 'resumes' : 'vacancies';
      router.push(
        `/${basePath}/${id}?showModal=true&adId=${id}&adType=${adType}`
      );
    } catch (error) {
      logError(error as ApolloError, {
        context: 'ActivateAd',
        adId: id,
        adType
      });
    }
  };

  const onPromote = () => {
    mainModal?.open?.();
  };

  const onEdit = () => {
    console.log('edit');
  };

  const { toast: copyUrlToast, onCopy } = useCopyUrl();

  return {
    confirmModal,
    mainModal,
    copyUrlToast,
    onDeactivateOrDeleteOpen,
    onDeactivateOrDeleteConfirm,
    onActivate,
    onPromote,
    onEdit,
    onCopy,
    isActionPending
  };
};
